/**
 * English: Detail view for a single device agent (merged agent + device list row).
 * Thai: มุมมองรายละเอียด device agent
 */
import { useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  CommandLineIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import PageShell from '../components/layout/PageShell';
import PageHeader from '../components/layout/PageHeader';
import CopyButton from '../components/shared/CopyButton';
import StatusBadge from '../components/shared/StatusBadge';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import { toast } from '../components/shared/Toast';
import { useAgentRows } from '../components/agent-management/useAgentRows';
import {
  chatLinkFor,
  controlLinkFor,
  deviceStatusVariant,
  displayDeviceType,
  agentManagementQuery,
} from '../components/agent-management/agentRowView';
import { CMMS_DETAIL_FIELD_ORDER, collectExtraCmmsKeys } from '../components/agent-management/cmmsDetailConfig';
import StationMemorySummaryPanel from '../components/agent-management/StationMemorySummaryPanel';
import AgentCompetencyDashboard from '../components/agent-management/AgentCompetencyDashboard';
import KnowledgePanel from '../components/agent-management/knowledge/KnowledgePanel';
import { useAgentManagementOrgContext } from '../components/agent-management/useAgentManagementOrgContext';
import { useAgentManagementScope } from '../hooks/useAgentManagementScope';
import { useStation } from '../context/StationContext';
import { useDeviceMemory } from '../hooks/useDeviceMemory';
import { useUpdateDeviceSettings } from '../hooks/useUpdateDeviceSettings';
import type { DeviceManageInfo } from '../types';

function DetailPanel({
  title,
  children,
  className = '',
  scrollable = false,
}: {
  title: string;
  children: ReactNode;
  className?: string;
  scrollable?: boolean;
}) {
  return (
    <section
      className={`flex min-h-0 flex-col rounded-xl border border-app-border/80 bg-app-surface shadow-sm ${className}`}
    >
      <header className="shrink-0 border-b border-app-border/60 bg-app-muted/15 px-4 py-3">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-app-text-dim">{title}</h2>
      </header>
      <div
        className={`flex-1 px-4 py-4 text-sm ${scrollable ? 'max-h-[28rem] overflow-y-auto overscroll-contain' : ''
          }`}
      >
        {children}
      </div>
    </section>
  );
}

function FieldRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-0.5 border-b border-app-border/40 py-2.5 last:border-0 last:pb-0 first:pt-0">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-app-text-dim">{label}</span>
      <div className="min-w-0 text-app-text">{value}</div>
    </div>
  );
}

function FieldGrid({ children }: { children: ReactNode }) {
  return <div className="min-w-0">{children}</div>;
}

function DisplayNameField({
  initialValue,
  onSave,
}: {
  initialValue: string | null | undefined;
  onSave: (value: string | null) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialValue ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    const trimmed = value.trim();
    const newValue = trimmed ? trimmed : null;
    if (newValue === (initialValue ?? null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(newValue);
      setEditing(false);
      toast.success('บันทึกชื่อแสดงผลเรียบร้อย');
    } catch {
      toast.error('บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }, [value, initialValue, onSave]);

  const handleCancel = useCallback(() => {
    setValue(initialValue ?? '');
    setEditing(false);
  }, [initialValue]);

  if (editing) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleSave();
              if (e.key === 'Escape') handleCancel();
            }}
            placeholder="ชื่อที่แสดงผล (Display name)"
            className="flex-1 min-w-0 px-3 py-1.5 rounded-lg border border-app-border bg-app-bg text-sm text-app-text placeholder:text-app-text-dim focus:outline-none focus:ring-2 focus:ring-app-accent/30"
            disabled={saving}
            autoFocus
          />
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-app-border bg-app-accent text-white text-xs font-medium hover:bg-app-accent/90 disabled:opacity-50"
          >
            {saving ? (
              <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <CheckIcon className="w-3.5 h-3.5" />
                บันทึก
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-app-border bg-app-bg text-app-text text-xs font-medium hover:bg-app-muted disabled:opacity-50"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
            ยกเลิก
          </button>
        </div>
        <p className="text-xs text-app-text-dim">
          {value.trim()
            ? `จะแสดงชื่อ "${value.trim()}" แทน "${initialValue ?? '—'}"`
            : 'เว้นว่าง = ไม่มีชื่อแสดงผล'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-app-text-sec truncate">{initialValue ?? '—'}</span>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-app-accent hover:bg-app-accent/10"
        title="แก้ไขชื่อแสดงผล"
      >
        <PencilIcon className="w-3 h-3" />
        แก้ไข
      </button>
    </div>
  );
}

function formatCmmsFieldValue(key: string, value: unknown): ReactNode {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' && value.trim() === '') return '—';
  if (key === 'cmms_pdf_url' || key === 'cmms_image_url') {
    const s = String(value).trim();
    if (!s) return '—';
    if (/^https?:\/\//i.test(s)) {
      return (
        <span className="flex flex-wrap items-center gap-2 min-w-0">
          <a
            href={s}
            target="_blank"
            rel="noopener noreferrer"
            className="text-app-accent hover:underline break-all text-left"
          >
            {s}
          </a>
          <CopyButton text={s} small />
        </span>
      );
    }
    return <code className="text-xs break-all text-app-text-sec">{s}</code>;
  }
  if (key === 'cmms_description') {
    return <span className="text-app-text-sec whitespace-pre-wrap break-words">{String(value)}</span>;
  }
  return <span className="text-app-text-sec break-words">{String(value)}</span>;
}

export default function AgentDetailPage() {
  const { t } = useTranslation();
  const { agentId: agentIdParam } = useParams<{ agentId: string }>();
  const targetId = agentIdParam ? decodeURIComponent(agentIdParam) : '';
  const scope = useAgentManagementScope();
  const { setActiveStationId } = useStation();
  const { rows, loading, awaitingStation, refresh } = useAgentRows(scope.stationId);
  const row = rows.find((r) => r.agent.id === targetId) ?? null;
  const { orgId, stationId } = useAgentManagementOrgContext();
  const listHref = `/agent-management${agentManagementQuery(scope.orgName, scope.stationId)}`;

  useEffect(() => {
    if (scope.stationId) {
      setActiveStationId(scope.stationId);
    }
  }, [scope.stationId, setActiveStationId]);

  const deviceMemory = useDeviceMemory(
    orgId,
    stationId,
    row?.deviceId ?? '',
    row?.agent.id,
  );

  const { updateDeviceSettings } = useUpdateDeviceSettings();

  const knowledgeUploadCountRef = useRef(0);
  const [knowledgeRefetchKey, setKnowledgeRefetchKey] = useState(0);
  const handleKnowledgeUpload = useCallback(() => {
    knowledgeUploadCountRef.current += 1;
    setKnowledgeRefetchKey(knowledgeUploadCountRef.current);
  }, []);

  const handleSaveDisplayName = useCallback(
    async (value: string | null) => {
      if (!row?.device?.device_db_id) return;
      await updateDeviceSettings(row.device.device_db_id, {
        display_name: value,
      });
      await refresh();
    },
    [row?.device?.device_db_id, updateDeviceSettings, refresh]
  );

  const busy = loading || awaitingStation;

  if (busy && !row) {
    return (
      <PageShell>
        <div className="flex items-center justify-center min-h-[240px]">
          <LoadingSpinner size="md" />
        </div>
      </PageShell>
    );
  }

  if (!busy && !row) {
    return (
      <PageShell>
        <PageHeader title={t('agents.notFound')} />
        <p className="text-sm text-app-text-dim mt-2 max-w-xl">
          {t('agents.notFoundScope', {
            agentId: targetId || t('agents.notFoundEmptyId'),
            org: scope.orgName || t('agents.scopeNotSet'),
            station: scope.stationId || t('agents.scopeNotSet'),
          })}
        </p>
        <Link
          to={listHref}
          className="inline-flex items-center gap-2 mt-6 text-sm font-medium text-app-accent hover:underline"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {t('agents.backToAgents')}
        </Link>
      </PageShell>
    );
  }
  if (!row) {
    return null;
  }

  const { agent, device, deviceId } = row;
  const color = typeof agent.color === 'string' ? agent.color : '#64748b';
  const dtype = displayDeviceType(row);
  const topic = device?.topic?.trim() || '';

  return (
    <PageShell wide>
      <div className="mb-4">
        <Link
          to={listHref}
          className="inline-flex items-center gap-1.5 text-sm text-app-accent hover:underline mb-3"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {t('agents.backToAgents')}
        </Link>        <PageHeader title={agent.name || deviceId} description={agent.id} />
      </div>

      <div className="mx-auto w-full space-y-6">
        <div className="overflow-hidden rounded-2xl border border-app-border/80 bg-app-surface shadow-sm">
          <div className="flex flex-col gap-4 border-b border-app-border/60 bg-gradient-to-r from-app-muted/25 via-app-surface to-app-muted/15 px-4 py-4 sm:px-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <span
                className="mt-1 h-4 w-4 shrink-0 rounded-full ring-2 ring-app-border/80"
                style={{ backgroundColor: color }}
                aria-hidden
              />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="truncate text-lg font-semibold text-ink">{agent.name || deviceId}</h2>
                  <span className="rounded-md bg-primary/12 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
                    {dtype}
                  </span>
                  {device?.device_status ? (
                    <StatusBadge variant={deviceStatusVariant(device.device_status)} dot>
                      {device.device_status}
                    </StatusBadge>
                  ) : null}
                </div>
                <p className="mt-0.5 truncate font-mono text-xs text-app-text-dim">{agent.id}</p>
                {topic ? (
                  <p className="mt-1 truncate font-mono text-[11px] text-app-text-sec" title={topic}>
                    {topic}
                  </p>
                ) : null}
                {(device?.kpi_score != null || device?.grade) && (
                  <p className="mt-1 text-xs tabular-nums text-app-text-sec">
                    KPI {device?.kpi_score != null ? String(device.kpi_score) : '—'}
                    {device?.grade != null && device.grade !== '' ? ` · ${String(device.grade)}` : ''}
                  </p>
                )}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Link
                to={chatLinkFor(row)}
                className="inline-flex items-center gap-2 rounded-lg border border-app-border bg-app-bg px-3 py-2 text-sm font-medium text-app-accent hover:bg-app-muted"
              >
                <ChatBubbleLeftRightIcon className="h-4 w-4" />
                Chat
              </Link>
              <Link
                to={controlLinkFor(row)}
                className="inline-flex items-center gap-2 rounded-lg border border-app-border bg-app-bg px-3 py-2 text-sm font-medium text-app-text hover:bg-app-muted"
              >
                <CommandLineIcon className="h-4 w-4" />
                Control
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 p-4 sm:p-5 lg:grid-cols-3 lg:gap-5 lg:items-start">
            <DetailPanel title={t('agents.detail.agentSection', { defaultValue: 'Agent' })}>
              <FieldGrid>
                <FieldRow
                  label={t('agents.detail.color', { defaultValue: 'Color' })}
                  value={
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="h-3.5 w-3.5 rounded-full ring-1 ring-app-border"
                        style={{ backgroundColor: color }}
                      />
                      <code className="text-xs text-app-text-sec">{color}</code>
                    </span>
                  }
                />
                <FieldRow
                  label={t('agents.detail.agentId', { defaultValue: 'Agent ID' })}
                  value={
                    <div className="flex min-w-0 items-center gap-2">
                      <code className="break-all text-xs">{agent.id}</code>
                      <CopyButton text={agent.id} small />
                    </div>
                  }
                />
                <FieldRow label={t('agents.detail.name', { defaultValue: 'Name' })} value={agent.name || '—'} />
                <FieldRow label={t('agents.detail.role', { defaultValue: 'Role' })} value={agent.role || '—'} />
                <FieldRow
                  label={t('agents.detail.department', { defaultValue: 'Department' })}
                  value={agent.department || '—'}
                />
                <FieldRow
                  label={t('agents.detail.description', { defaultValue: 'Description' })}
                  value={
                    agent.description != null ? (
                      <span className="text-app-text-sec leading-relaxed">{String(agent.description)}</span>
                    ) : (
                      '—'
                    )
                  }
                />
              </FieldGrid>
            </DetailPanel>

            <DetailPanel title={t('agents.detail.deviceSection', { defaultValue: 'Device & MQTT' })}>
              <FieldGrid>
                <FieldRow
                  label={t('agents.detail.deviceId', { defaultValue: 'Device ID' })}
                  value={<code className="text-xs">{deviceId}</code>}
                />
                {device?.device_db_id != null ? (
                  <FieldRow
                    label={t('agents.detail.catalogDbId', { defaultValue: 'Catalog DB id' })}
                    value={
                      <span className="tabular-nums text-app-text-sec">
                        {String(device.device_db_id)}
                        <span className="mt-0.5 block text-[10px] text-app-text-dim">
                          devices.id → device_manage.device_id
                        </span>
                      </span>
                    }
                  />
                ) : null}
                {device?.device_db_id != null ? (
                  <FieldRow
                    label={t('agents.detail.displayName', { defaultValue: 'Display name' })}
                    value={
                      <DisplayNameField
                        initialValue={device?.display_name}
                        onSave={handleSaveDisplayName}
                      />
                    }
                  />
                ) : null}
                <FieldRow
                  label={t('agents.detail.deviceType', { defaultValue: 'Device type' })}
                  value={dtype}
                />
                <FieldRow
                  label={t('agents.detail.organization', { defaultValue: 'Organization' })}
                  value={device?.org_name ? String(device.org_name) : '—'}
                />
                <FieldRow
                  label={t('agents.detail.mqttTopic', { defaultValue: 'MQTT topic' })}
                  value={
                    topic ? (
                      <div className="flex min-w-0 items-start gap-2">
                        <code className="break-all text-xs text-app-text-sec">{topic}</code>
                        <CopyButton text={topic} small />
                      </div>
                    ) : (
                      '—'
                    )
                  }
                />
                <FieldRow
                  label={t('agents.detail.status', { defaultValue: 'Status' })}
                  value={
                    device?.device_status ? (
                      <StatusBadge variant={deviceStatusVariant(device.device_status)} dot>
                        {device.device_status}
                      </StatusBadge>
                    ) : (
                      '—'
                    )
                  }
                />
                <FieldRow
                  label={t('agents.detail.kpiGrade', { defaultValue: 'KPI / Grade' })}
                  value={
                    <span className="tabular-nums text-app-text-sec">
                      {device?.kpi_score != null ? String(device.kpi_score) : '—'}
                      {device?.grade != null && device.grade !== '' ? ` / ${String(device.grade)}` : ''}
                    </span>
                  }
                />
                {device?.grade_description_th ? (
                  <FieldRow
                    label={t('agents.detail.gradeTh', { defaultValue: 'Grade (TH)' })}
                    value={<span className="text-app-text-sec">{String(device.grade_description_th)}</span>}
                  />
                ) : null}
              </FieldGrid>
            </DetailPanel>

            <DetailPanel
              title={t('agents.detail.cmmsSection', { defaultValue: 'CMMS (device_manage)' })}
              scrollable={Boolean(device?.device_manage)}
              className="lg:min-h-[12rem]"
            >
              {device?.device_manage ? (
                <FieldGrid>
                  {((): ReactNode => {
                    const dm = device.device_manage as DeviceManageInfo & Record<string, unknown>;
                    const extras = collectExtraCmmsKeys(dm);
                    return (
                      <>
                        {CMMS_DETAIL_FIELD_ORDER.map(({ key, label }) => (
                          <FieldRow key={key} label={label} value={formatCmmsFieldValue(key, dm[key])} />
                        ))}
                        {extras.map((key) => (
                          <FieldRow key={key} label={key} value={formatCmmsFieldValue(key, dm[key])} />
                        ))}
                      </>
                    );
                  })()}
                </FieldGrid>
              ) : (
                <div className="space-y-2 text-xs leading-relaxed text-app-text-dim">
                  <p>
                    {t('agents.detail.cmmsEmpty', {
                      defaultValue: 'ไม่มีแถว CMMS สำหรับอุปกรณ์นี้ — อ่านจาก PostgreSQL device_manage',
                    })}
                  </p>
                  <p>
                    {t('agents.detail.cmmsLinkHint', {
                      defaultValue:
                        'เชื่อมด้วย device_manage.device_id = devices.id (Catalog DB id ด้านซ้าย) ไม่ใช่แค่ป้าย Device ID',
                    })}
                  </p>
                  <p className="rounded-lg border border-dashed border-app-border/80 bg-app-muted/10 p-2.5 font-mono text-[10px]">
                    {deviceId}
                  </p>
                </div>
              )}
            </DetailPanel>
          </div>
        </div>

        <div className="rounded-2xl border border-app-border/80 bg-app-surface p-4 shadow-sm sm:p-5">
          <AgentCompetencyDashboard
            agentId={agent.id}
            dailyItems={deviceMemory.daily.data?.items ?? []}
            kpiScore={device?.kpi_score ?? null}
            loading={deviceMemory.daily.loading}
          />
        </div>

        {orgId && stationId ? (
          <KnowledgePanel
            orgId={orgId}
            stationId={stationId}
            deviceId={deviceId}
            deviceColor={color}
            includeStationShared
            onUploadSuccess={handleKnowledgeUpload}
          />
        ) : null}

        <StationMemorySummaryPanel
          deviceId={deviceId}
          agentId={agent.id}
          embedded
          knowledgeRefetchKey={knowledgeRefetchKey}
        />
      </div>
    </PageShell>
  );
}
